import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { CalificadorDeResultado } from "@/components/laboratorio/CalificadorDeResultado";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { useCalificar, useExperimento, useSeccionesRedactables, useVolverACorrer } from "@/hooks/useLaboratorio";
import { getApiErrorMessage } from "@/lib/api";
import { formatearCostoUsd, formatearTokens } from "@/lib/formatoDeConsumo";
import {
  compararParrafos,
  formatearDuracion,
  parrafosDelTexto,
  resumenDelLado,
  veredictoDelExperimento,
  type Calificacion,
  type CasoDelExperimento,
  type ExperimentoCompleto,
  type ResultadoDelExperimento,
  type TramoDePalabras,
} from "@/lib/laboratorio";
import { cn } from "@/lib/utils";

const PUNTO: Record<Calificacion | "sin_calificar", string> = {
  bueno: "bg-primary",
  regular: "bg-[#e0a63a]",
  malo: "bg-[#c23a32]",
  sin_calificar: "bg-ink-ghost",
};

/** El experimento abierto: sus casos, las tres columnas, la comparación y la calificación. */
export function DetalleDelExperimento({ id }: { id: number }) {
  const experimento = useExperimento(id);
  const secciones = useSeccionesRedactables();
  const volver = useVolverACorrer();
  const [elegido, setElegido] = useState(0);
  const [resaltar, setResaltar] = useState(false);

  /* El aviso de «listo» salta solo cuando se VE pasar de corriendo a terminado,
     no cuando se abre un experimento que ya había terminado. */
  const anterior = useRef<string | undefined>(undefined);
  const datos = experimento.data;
  useEffect(() => {
    if (datos && anterior.current === "corriendo" && datos.estado === "terminado") {
      toast.success(`Experimento listo · ${datos.casos.length} ${datos.casos.length === 1 ? "caso" : "casos"} · ${formatearCostoUsd(datos.costo_usd)}`);
    }
    anterior.current = datos?.estado;
  }, [datos]);

  if (experimento.isPending) return <Skeleton className="h-96 rounded-[1.25rem]" />;
  if (experimento.isError || !datos) return <p className="text-meta text-danger-texto">No pudimos cargar el experimento.</p>;

  const nombreDeLaSeccion = secciones.data?.find((s) => s.codigo === datos.item_codigo)?.nombre ?? datos.item_codigo;
  const caso = datos.casos[Math.min(elegido, datos.casos.length - 1)];
  const corriendo = datos.estado === "corriendo";
  const veredicto = veredictoDelExperimento(datos.resumen);
  const etiquetaPublicada = datos.publicada?.etiqueta ?? "la publicada";

  return (
    <div className="flex flex-col gap-3">
      <Card variant="marcada" className="flex-row flex-wrap items-center gap-3.5 rounded-[1.25rem] px-[1.125rem] py-3.5">
        <div className="min-w-0">
          <p className="rotulo-seccion">Experimento</p>
          <h3 className="text-ink text-[0.875rem] font-extrabold">
            {nombreDeLaSeccion} · Borrador contra {etiquetaPublicada}
          </h3>
        </div>

        <div role="group" aria-label="Casos del experimento" className="flex flex-wrap gap-1.5">
          {datos.casos.map((c, i) => (
            <button
              key={c.caso_id ?? c.nombre}
              type="button"
              data-calificacion={c.candidata?.calificacion ?? "sin_calificar"}
              aria-pressed={i === elegido}
              onClick={() => setElegido(i)}
              className={cn(
                "bg-card inline-flex h-8 items-center gap-1.5 rounded-[0.625rem] border-[1.5px] border-[#e4e5e2] px-[0.6875rem] text-[0.75rem] font-bold",
                i === elegido ? "border-ink bg-ink text-background" : "hover:border-ink-ghost",
              )}
            >
              <span aria-hidden className={cn("size-[0.4375rem] rounded-full", PUNTO[c.candidata?.calificacion ?? "sin_calificar"])} />
              {c.nombre}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-2 text-[0.75rem] font-bold">
            <Switch aria-label="Resaltar diferencias" checked={resaltar} onCheckedChange={setResaltar} className="h-[1.125rem] w-[1.875rem] [&>span]:size-3.5 data-[state=checked]:[&>span]:translate-x-[0.8125rem]" />
            <span className="text-ink-secundario">Resaltar diferencias</span>
          </label>
          <Button
            type="button"
            variant="secondary"
            className="rounded-[0.75rem] text-[0.78125rem] font-bold"
            disabled={corriendo || volver.isPending}
            onClick={() =>
              volver.mutate(datos.id, {
                onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos volver a correr el experimento.")),
              })
            }
          >
            {corriendo ? (
              <>
                <Spinner /> Corriendo {datos.casos.length} {datos.casos.length === 1 ? "caso" : "casos"}…
              </>
            ) : (
              "Volver a correr"
            )}
          </Button>
        </div>
      </Card>

      {caso ? <ComparacionDelCaso experimento={datos} caso={caso} resaltar={resaltar} /> : null}

      <Card variant="marcada" className="flex-row flex-wrap items-center justify-between gap-4 rounded-[1.25rem] px-[1.125rem] py-3.5">
        <div className="flex flex-col gap-1">
          <p className="rotulo-seccion">Resumen</p>
          <p className="text-ink-secundario font-mono text-[0.75rem] font-semibold">Borrador · {resumenDelLado(datos.resumen.candidata)}</p>
          <p className="text-ink-secundario font-mono text-[0.75rem] font-semibold">
            {etiquetaPublicada} · {resumenDelLado(datos.resumen.publicada)}
          </p>
        </div>
        <p
          className={cn(
            "text-[0.78125rem] font-bold",
            veredicto.tono === "success" && "text-success-soft-foreground",
            veredicto.tono === "warning" && "text-warning-texto",
            veredicto.tono === "danger" && "text-danger-texto",
          )}
        >
          {veredicto.texto}
        </p>
      </Card>
    </div>
  );
}

function ComparacionDelCaso({
  experimento,
  caso,
  resaltar,
}: {
  experimento: ExperimentoCompleto;
  caso: CasoDelExperimento;
  resaltar: boolean;
}) {
  const calificar = useCalificar(experimento.id);
  const etiquetaPublicada = experimento.publicada?.etiqueta ?? "Publicada";

  const nuevo = parrafosDelTexto(caso.candidata?.texto ?? null);
  const publicado = parrafosDelTexto(caso.publicada?.texto ?? null);
  const real = parrafosDelTexto(caso.real.texto);
  const filas = Math.max(nuevo.length, publicado.length, real.length, 1);

  function calificarResultado(resultado: ResultadoDelExperimento, calificacion: Calificacion, comentario: string | null) {
    calificar.mutate(
      { resultadoId: resultado.id, calificacion, comentario },
      { onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos guardar la calificación.")) },
    );
  }

  return (
    <>
      <div className="grid gap-3 lg:grid-cols-3">
        <ColumnaDeResultado titulo="Borrador" marca="nuevo" tono="info" borde="border-[#cfe0f8]" resultado={caso.candidata} />
        <ColumnaDeResultado titulo={`${etiquetaPublicada} publicada`} marca="actual" tono="neutral" resultado={caso.publicada} />
        <ColumnaDelTextoReal caso={caso} />
      </div>

      <Card variant="marcada" className="gap-0 overflow-hidden rounded-[1.25rem] py-0">
        {Array.from({ length: filas }, (_, i) => {
          const marcado = resaltar ? compararParrafos(nuevo[i] ?? "", publicado[i] ?? null) : null;
          const marcadoPublicado = resaltar ? compararParrafos(nuevo[i] ?? "", publicado[i] ?? null).publicada : null;

          return (
            <div key={i} className="border-divisor-fuerte grid grid-cols-3 border-b last:border-b-0">
              <CeldaDeTexto tramos={marcado?.borrador ?? null} texto={nuevo[i]} marca="nuevo" />
              <CeldaDeTexto tramos={marcadoPublicado} texto={publicado[i]} marca="quitado" />
              <CeldaDeTexto
                tramos={null}
                texto={real[i]}
                vacio={i === 0 && real.length === 0 ? "Este informe aún no se emite: no hay texto real para comparar." : undefined}
                fondo
              />
            </div>
          );
        })}

        <div className="bg-superficie-tenue border-border grid grid-cols-3 border-t">
          <div className="px-[1.125rem] py-3.5">
            {caso.candidata ? (
              <CalificadorDeResultado
                key={caso.candidata.id}
                nombre="del borrador"
                resultado={caso.candidata}
                onCalificar={(c, t) => calificarResultado(caso.candidata!, c, t)}
              />
            ) : null}
          </div>
          <div className="border-divisor-fuerte border-l px-[1.125rem] py-3.5">
            {caso.publicada ? (
              <CalificadorDeResultado
                key={caso.publicada.id}
                nombre={`de ${etiquetaPublicada}`}
                resultado={caso.publicada}
                onCalificar={(c, t) => calificarResultado(caso.publicada!, c, t)}
              />
            ) : null}
          </div>
          <div className="border-divisor-fuerte text-ink-muted border-l px-[1.125rem] py-3.5 text-[0.75rem] font-semibold">
            Referencia: lo que Consultek entregó al cliente en este informe. No se califica.
          </div>
        </div>
      </Card>
    </>
  );
}

function ColumnaDeResultado({
  titulo,
  marca,
  tono,
  borde,
  resultado,
}: {
  titulo: string;
  marca: string;
  tono: "info" | "neutral";
  borde?: string;
  resultado: ResultadoDelExperimento | null;
}) {
  return (
    <Card variant="marcada" className={cn("gap-2.5 rounded-[1.25rem] px-4 py-3.5", borde)}>
      <div className="flex items-center gap-2">
        <h4 className="text-[0.8125rem] font-extrabold">{titulo}</h4>
        <Etiqueta tono={tono} className="font-mono font-bold">
          {marca}
        </Etiqueta>
      </div>
      {resultado === null ? (
        <p className="text-meta text-ink-hint">Sin resultado.</p>
      ) : resultado.estado === "pendiente" ? (
        <p className="text-meta text-ink-medio flex items-center gap-2">
          <Spinner /> Generando…
        </p>
      ) : resultado.estado === "error" ? (
        <p className="text-meta text-danger-texto">No se pudo redactar este texto{resultado.codigo_error ? ` (${resultado.codigo_error})` : ""}.</p>
      ) : (
        <>
          <p className="text-ink-muted font-mono text-[0.6875rem] font-semibold">
            {resultado.palabras ?? 0} palabras · {formatearTokens((resultado.tokens_entrada ?? 0) + (resultado.tokens_salida ?? 0))} tokens ·{" "}
            {formatearCostoUsd(resultado.costo_usd)} · {formatearDuracion(resultado.latencia_ms)}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {resultado.chequeos.map((c) => (
              <Etiqueta key={c.codigo} tono={c.ok ? "success" : "danger"} title={c.detalle ?? undefined} className="font-bold">
                {c.ok ? "✓" : "✕"} {c.ok ? c.etiqueta : (c.detalle ?? c.etiqueta)}
              </Etiqueta>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function ColumnaDelTextoReal({ caso }: { caso: CasoDelExperimento }) {
  const hay = caso.real.texto !== null;

  return (
    <Card variant="marcada" className="gap-2.5 rounded-[1.25rem] px-4 py-3.5">
      <div className="flex items-center gap-2">
        <h4 className="text-[0.8125rem] font-extrabold">Escrito por Consultek</h4>
        {hay && caso.informe_numero ? (
          <Etiqueta tono="success" className="font-mono font-bold">
            {caso.informe_numero}
          </Etiqueta>
        ) : (
          <Etiqueta tono="tenue" className="font-bold">
            sin texto
          </Etiqueta>
        )}
      </div>
      <p className="text-ink-muted font-mono text-[0.6875rem] font-semibold">
        {hay ? `${caso.real.texto!.split(/\s+/).filter(Boolean).length} palabras` : "—"}
      </p>
    </Card>
  );
}

function CeldaDeTexto({
  tramos,
  texto,
  marca,
  vacio,
  fondo,
}: {
  tramos: TramoDePalabras[] | null;
  texto: string | undefined;
  marca?: "nuevo" | "quitado";
  vacio?: string;
  fondo?: boolean;
}) {
  return (
    <div
      className={cn(
        "border-divisor-fuerte px-[1.125rem] py-3.5 text-[0.8125rem] leading-[1.65] text-[#24262a] not-first:border-l",
        fondo && "bg-superficie-tenue",
      )}
    >
      {vacio ? (
        <span className="text-ink-hint">{vacio}</span>
      ) : tramos ? (
        tramos.map((t, i) =>
          t.marca ? (
            <mark
              key={i}
              data-diff={marca}
              className={cn("rounded-[0.1875rem] text-inherit", marca === "nuevo" ? "bg-success-soft" : "bg-danger-soft")}
            >
              {t.texto}
            </mark>
          ) : (
            <span key={i}>{t.texto}</span>
          ),
        )
      ) : (
        texto
      )}
    </div>
  );
}
